import React, { useContext } from 'react'
import { useIntl } from 'react-intl'
import { ViewStyle } from 'react-native'

import { noop } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import { GLOBAL_HEADER_HEIGHT } from '@lyrahealth-inc/shared-app-logic/src/features/header/constants'
import {
  FavoritedProvidersEntrypointNavProps,
  NavigationItem,
} from '@lyrahealth-inc/shared-app-logic/src/features/header/types'
import { getIsActiveNavItem } from '@lyrahealth-inc/shared-app-logic/src/features/header/utils'

import { ButtonSize } from '../../atoms/baseButton/BaseButton'
import { HeaderMessagingIcon } from '../../atoms/headerMessagingIcon/HeaderMessagingIcon'
import { ArrowIcon, ArrowIconDirection } from '../../atoms/icons/ArrowIcon'
import { LyraLogo } from '../../atoms/icons/LyraLogo'
import { SearchStarIcon } from '../../atoms/icons/SearchStarIcon'
import { PressableOpacity } from '../../atoms/pressableOpacity/PressableOpacity'
import { TertiaryIconButton } from '../../atoms/tertiaryIconButton/TertiaryIconButton'
import { Title } from '../../atoms/title/Title'
import { IS_WEB } from '../../constants'
import { AppContext } from '../../context'
import { FavoritedProvidersStarIconButton } from '../../molecules/favoritedProvidersStarIconButton/FavoritedProvidersStarIconButton'
import { getFocusBoxShadow } from '../../styles/commonStyles'
import { TitleSize } from '../../styles/typeStyles'
import { RowView } from '../../templates/content/CommonViews'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'
import { HeaderNavMenu, HeaderNavMenuProps } from '../headerNavMenu/HeaderNavMenu'
import { AuroraIconCircle } from '../lightHomeToolbar/utils'

export type GlobalHeaderProps = {
  /** Drives the transparent → blur/gradient transition. Pass from useIsScreenScrolled(). */
  isScrolled: boolean
  // Left
  onBackPressed?: () => void
  onLogoPressed?: () => void
  logoFillColor?: string
  // Center — only shown when onBackPressed is set
  title?: string | JSX.Element
  navItems?: NavigationItem[]
  currentPathname?: string
  currentScreen?: string
  // Right: action icons
  showMessages?: boolean
  onMessagesPressed?: () => void
  messagesPopover?: JSX.Element
  unreadMessages?: number
  showAICoachIcon?: boolean
  shouldShowSearch?: boolean
  onSearchPressed?: () => void
  shouldShowNavMenu?: boolean
  headerNavMenuProps?: HeaderNavMenuProps
  shouldShowCareNavigatorHeaderEntrypoint?: boolean
  careNavigatorEntryPoint?: React.ReactElement
  careNavigatorModal?: React.ReactElement
  favoritedProvidersEntrypointNavProps?: FavoritedProvidersEntrypointNavProps
  topInset?: number
}

export const OuterContainer = styled.View<{
  topInset: number
}>(({ topInset }) => ({
  width: '100%',
  paddingTop: IS_WEB ? 0 : topInset,
  backgroundColor: 'transparent',
  ...(IS_WEB && {
    position: 'sticky',
    top: 0,
    zIndex: 1,
    height: GLOBAL_HEADER_HEIGHT,
  }),
}))

export const HeaderRow = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  height: GLOBAL_HEADER_HEIGHT,
  paddingHorizontal: theme.breakpoints.isMobileSized ? theme.spacing['16px'] : theme.spacing['32px'],
}))

const LeftSection = styled.View({
  flexDirection: 'row',
  alignItems: 'center',
  zIndex: 1,
})

const NavItemsContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  flexDirection: 'row',
  marginLeft: theme.spacing['48px'],
  gap: theme.spacing['24px'],
}))

const NavItemButton = styled(PressableOpacity)<{ theme: ThemeType }>(({ theme }) => ({
  padding: theme.spacing['8px'],
  borderRadius: theme.spacing['8px'],
}))

const NavItemActiveUnderline = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  position: 'absolute',
  left: 0,
  right: 0,
  bottom: -1.5,
  height: '1.5px',
  width: '59px',
  backgroundColor: theme.colors.iconDefault,
  borderRadius: '20px',
  marginHorizontal: 'auto',
}))

const RightSection = styled(RowView)({
  marginLeft: 'auto',
  alignItems: 'center',
  gap: 16,
})

const LogoButton = styled(PressableOpacity)<{ theme: ThemeType }>(({ theme }) => ({
  padding: `${theme.spacing['4px']} ${theme.spacing['8px']}`,
  borderRadius: theme.spacing['8px'],
}))

const IconButtonWrapper = styled.View({
  position: 'relative',
})

const TitleContainer = styled.View({
  position: 'absolute',
  left: 0,
  right: 0,
  alignItems: 'center',
})

/**
 * Builds the inner `headerContents` JSX (left logo / back, desktop nav items, right action
 * icons) and computes the active nav tab. The output is identical across native and web —
 * platform-specific render files (`GlobalHeader.tsx` / `GlobalHeader.web.tsx`) drop the
 * resulting JSX inside their platform-specific blur/sticky wrappers.
 */
export const useHeaderContents = (props: GlobalHeaderProps) => {
  const {
    onBackPressed,
    onLogoPressed,
    logoFillColor,
    title,
    navItems,
    currentPathname,
    currentScreen,
    showMessages = false,
    onMessagesPressed,
    messagesPopover,
    unreadMessages = 0,
    showAICoachIcon,
    shouldShowSearch,
    onSearchPressed,
    shouldShowNavMenu = true,
    headerNavMenuProps,
    shouldShowCareNavigatorHeaderEntrypoint = false,
    careNavigatorEntryPoint,
    favoritedProvidersEntrypointNavProps,
  } = props

  const { colors, isDarkMode, breakpoints } = useTheme() as ThemeType
  const { formatMessage } = useIntl()
  const { isSmartSearchEnabled } = useContext(AppContext)

  const pressableStyle = {
    hoveredBackgroundColor: colors.tertiaryButtonBackgroundHover,
    pressedBackgroundColor: colors.tertiaryButtonBackgroundPressed,
    styleByState: (pressed: boolean, focusVisible: boolean): ViewStyle => {
      const style: ViewStyle = {}
      if (pressed) {
        style.boxShadow = getFocusBoxShadow({ colors, outerBoxShadowSize: '6px' }).boxShadow
        style.backgroundColor = colors.tertiaryButtonBackgroundPressed
      }
      if (focusVisible && !pressed) {
        style.backgroundColor = colors.tertiaryButtonBackgroundHover
      }
      return style
    },
  }

  // ── Left: web has logo or back button, mobile has back button or empty ──────────────────────────────────────────────

  const logoOrBack = onBackPressed ? (
    <PressableOpacity
      onPress={onBackPressed}
      hitSlop={10}
      testID={tID('GlobalHeader-back-button')}
      accessibilityLabel={formatMessage({ defaultMessage: 'Go back', description: 'Button to go back' })}
    >
      <ArrowIcon fillColor={colors.iconDefault} direction={ArrowIconDirection.LEFT} />
    </PressableOpacity>
  ) : breakpoints.isMobileSized ? null : (
    <LogoButton
      onPress={onLogoPressed}
      accessibilityRole='button'
      accessibilityLabel={formatMessage({
        defaultMessage: 'Lyra Logo',
        description: 'Button that takes user to home page',
      })}
      testID={tID('GlobalHeader-lyra-logo')}
      {...pressableStyle}
    >
      <LyraLogo
        width={54}
        height={31}
        fillColor={logoFillColor ?? (isDarkMode ? colors.textInverse : colors.iconDefault)}
      />
    </LogoButton>
  )

  // ── Desktop nav items (inline) ────────────────────────────────────────────

  const desktopNavItems =
    navItems && breakpoints.isMinWidthLaptop && !onBackPressed ? (
      <NavItemsContainer role='list'>
        {navItems.map((item) => {
          const isActive = getIsActiveNavItem(item, currentPathname, currentScreen)
          return item.condition?.() ? (
            <NavItemButton
              key={item.id}
              testID={tID(`GlobalHeader-${item.id}`)}
              onPress={item.onPressItem}
              role='listitem'
              {...pressableStyle}
            >
              <Title
                text={item.text}
                size={TitleSize.XSMALL}
                regular
                color={isActive ? colors.textActive : colors.textPrimary}
                noRole
              />
              {isActive && <NavItemActiveUnderline testID={tID(`GlobalHeader-${item.id}-active`)} />}
            </NavItemButton>
          ) : null
        })}
      </NavItemsContainer>
    ) : null

  // ── Right: action icons ───────────────────────────────────────────────────

  const shouldShowFavoritedProviders = favoritedProvidersEntrypointNavProps?.shouldShowFavoritedProvidersStarIcon
  const shouldShowCareNav = shouldShowCareNavigatorHeaderEntrypoint && careNavigatorEntryPoint

  const messageButton = messagesPopover ? (
    <>{messagesPopover}</>
  ) : onMessagesPressed ? (
    <HeaderMessagingIcon
      showGenAi={showAICoachIcon && unreadMessages === 0}
      showUnread={unreadMessages > 0}
      onPress={onMessagesPressed}
      hitSlop={10}
      testID={tID('MessageIcon')}
    />
  ) : null

  const searchButton = (
    <TertiaryIconButton
      size={ButtonSize.SMALL}
      style={{ padding: 0 }}
      leftIcon={
        <AuroraIconCircle>
          <SearchStarIcon />
        </AuroraIconCircle>
      }
      onPress={onSearchPressed ?? noop}
      testID={tID('GlobalHeader-search-button')}
      accessibilityLabel={formatMessage({ defaultMessage: 'Search', description: 'Button to search' })}
    />
  )

  const rightSection = (
    <RightSection>
      {shouldShowFavoritedProviders && breakpoints.isMinWidthTablet && (
        <IconButtonWrapper>
          <AuroraIconCircle>
            <FavoritedProvidersStarIconButton
              isNavBarButton
              onPressStarIconButton={() =>
                favoritedProvidersEntrypointNavProps!.onFavoritedProvidersStarIconPressed({
                  isFromHamburgerMenu: false,
                })
              }
              isFilled={favoritedProvidersEntrypointNavProps!.isFavoritedProvidersStarIconFilled}
              onIconHover={favoritedProvidersEntrypointNavProps?.onHoverForIconExplanation ?? noop}
              buttonStyle={{ padding: 12 }}
            />
          </AuroraIconCircle>
        </IconButtonWrapper>
      )}
      {shouldShowCareNav && careNavigatorEntryPoint && (
        <IconButtonWrapper>{React.cloneElement(careNavigatorEntryPoint, { lightBgColor: false })}</IconButtonWrapper>
      )}
      {showMessages && messageButton}
      {shouldShowSearch && isSmartSearchEnabled && searchButton}
      {shouldShowNavMenu && headerNavMenuProps && (
        <HeaderNavMenu
          {...headerNavMenuProps}
          highlightTrigger
          shouldShowFavoritedProvidersStarIcon={() => shouldShowFavoritedProviders ?? false}
          handleFavoritedProvidersStarIconPress={
            favoritedProvidersEntrypointNavProps?.onFavoritedProvidersStarIconPressed
          }
        />
      )}
    </RightSection>
  )

  // ── Header row contents ───────────────────────────────────────────────────

  const headerContents = (
    <>
      <LeftSection>
        {logoOrBack}
        {desktopNavItems}
      </LeftSection>
      {title && onBackPressed && (
        <TitleContainer pointerEvents='none'>
          <Title text={title} size={TitleSize.SMALL} textAlign='center' />
        </TitleContainer>
      )}
      {rightSection}
    </>
  )

  // ── Active nav tab (for FloatingTabBar) ───────────────────────────────────

  const activeNavTab = navItems?.find(
    (item) => item.condition?.() && getIsActiveNavItem(item, currentPathname, currentScreen),
  )

  return { headerContents, activeNavTab }
}
